import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import Container from 'reactstrap/lib/Container';
import { Link } from 'site-modules/shared/components/link/link';
import { FilmstripCarousel } from 'site-modules/shared/components/core-page/sponsored-cars/filmstrip-carousel/filmstrip-carousel';
import EdmundsLogo from 'client/images/edmunds-logo-226.png';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { fireBuyingGuideSegmentSelect } from 'client/engagement-handlers/home-engagement-handler/home-engagement-handler';

import './buying-guide.scss';

const GAP = '16px';

function getOptionalImageSource(value) {
  if (value) {
    return value;
  }
  return null;
}

export function BuyingGuideUI({ content = DEFAULT_CONTENT, isMobile = false, breakpoint = null }) {
  const [isXlUp, setIsXlUp] = useState(false);
  const ref = useRef();
  const slides = (content && content.children()) || [];
  const slideWidth = isMobile ? 380 : 376;
  const showArrowsWhenXl = slides.length > 4;

  const DATA_TRACKING_ID = 'buying_guide_click';
  const CREATIVE_ID = 'edm-entry-buying-guide-module';

  const fireTracking = useCallback(event => {
    const creativeId = CREATIVE_ID;
    const trackingValue = event.currentTarget.dataset.trackingValue;
    const index = event.currentTarget.dataset.index;
    const trackingData = { trackingValue, creativeId, index };
    fireBuyingGuideSegmentSelect(trackingData);
  }, []);

  const slidesToShow = isXlUp ? slides.length - 1 : slides.length;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs isXlUp with breakpoint prop change
    setIsXlUp(['xl'].includes(breakpoint));
  }, [breakpoint]);

  useEffect(() => {
    if (!isXlUp) {
      ref.current?.querySelector('.slick-list')?.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [isXlUp]);

  if (!slides.length) {
    return null;
  }

  const renderSlideMapping = (tempSlides, isSliced = false) =>
    tempSlides.map((slide, index) => (
      <article
        key={`${slide.metadata('title').value()}-${tempSlides.length}`}
        className={classnames('pos-r slick-slide me-0 d-inline-block', {
          'ps-2_5 ps-sm-0': index === 0,
          'is-mobile': isMobile,
          'me-1': index !== tempSlides.length - 1,
        })}
        style={{
          width: index === 0 ? `${slideWidth + 40}px` : `${slideWidth}px`,
          whiteSpace: 'normal',
          maxWidth: 'none',
          flex: '1 1 0%',
        }}
      >
        <Link
          className="category-link"
          to={slide.metadata('link').value()}
          data-tracking-id={DATA_TRACKING_ID}
          data-tracking-value={`${slide.metadata('title').value()} - link`}
          data-index={isSliced ? index + 1 : index}
          onClick={fireTracking}
          rel="nofollow"
        >
          <span className="visually-hidden">
            {slide.metadata('ctaTitle').value()} for {slide.title}
          </span>
          <figure className="pos-r mb-0 image-container rounded-16">
            <img
              className="rounded-16"
              src={getOptionalImageSource(slide.metadata('imageSrc').value())}
              alt=""
              width="100%"
              style={{
                objectFit: 'cover',
              }}
            />
          </figure>
          <h3 className="size-20 mt-1 text-cool-gray-10 mb-0_25 hoverable-link">{slide.metadata('title').value()}</h3>
          <div className="text-cool-gray-30 mb-0">{slide.metadata('description').value()}</div>
          <div className="d-flex" />
        </Link>
      </article>
    ));

  return (
    <article className="buying-guide-module mx-auto py-md-2 mb-2 mt-md-3 overflow-x-hidden" ref={ref}>
      <Container className="px-0 py-2">
        <section className="d-flex">
          <header
            className="px-2_5 ps-sm-0_5 pe-sm-1"
            style={{
              flex: '1 1 0%',
            }}
          >
            <figure
              className="pb-0_25 mb-0_25"
              style={{
                width: '134px',
                borderBottom: '2px solid #007EE5',
              }}
            >
              <img src={EdmundsLogo} alt="" width={134} />
            </figure>
            <h2
              className="text-cool-gray-10 mb-1"
              style={{
                fontSize: '56px',
                lineHeight: '60px',
              }}
            >
              {content.metadata('title').value().toUpperCase()}
            </h2>
            <ContentFragment classes="text-cool-gray-40 size-24 pb-1_5">
              {content.metadata('description').value()}
            </ContentFragment>
            <ul className="text-cool-gray-30 size-20 ps-2_5 pe-0_5 mb-1_5">
              {content
                .metadata('bullets')
                .value()
                .split('|')
                .map(bullet => (
                  <li className="pb-1" key={bullet}>
                    {bullet}
                  </li>
                ))}
            </ul>
          </header>
          <div
            className={classnames('hidden-xl-down mb-2 pos-r', {
              'me-0_25': showArrowsWhenXl,
              'ps-0_5 me-0_75': !showArrowsWhenXl,
            })}
            style={{
              width: `${slideWidth * 2}px`,
              aspectRatio: '16 / 9',
            }}
          >
            <Link
              className="category-link"
              to={slides[0].metadata('link').value()}
              data-tracking-id={DATA_TRACKING_ID}
              data-tracking-value={`${slides[0].metadata('title').value()} - link`}
              data-index={0}
              onClick={fireTracking}
              rel="nofollow"
            >
              <span className="visually-hidden">
                {slides[0].metadata('ctaTitle').value()} for {slides[0].title}
              </span>
              <figure className="pos-r mb-0 image-container rounded-16">
                <img
                  className="rounded-16"
                  src={getOptionalImageSource(slides[0].metadata('imageSrc').value())}
                  alt=""
                  width="100%"
                  style={{
                    objectFit: 'cover',
                  }}
                />
              </figure>
              <div
                className="pos-a top-0 info-overlay h-100 px-2 py-3_5 d-flex flex-column"
                style={{
                  width: '257px',
                }}
              >
                <h3 className="size-24 text-white text-shadow-contrast">{slides[0].metadata('title').value()}</h3>
                <p className="text-white mt-1 text-shadow-contrast">{slides[0].metadata('description').value()}</p>
                <div className="d-flex" />
                <div
                  className="mt-auto btn text-white rounded-20 text-shadow-contrast"
                  style={{
                    border: '1px solid #fff',
                  }}
                >
                  {slides[0].metadata('ctaTitle').value()}
                </div>
              </div>
            </Link>
          </div>
        </section>
        <FilmstripCarousel
          totalAdCount={slidesToShow}
          showArrows={!(isXlUp && slides.length < 5)}
          arrowTabIndex={-1}
          classes={classnames('ms-sm-0_5', {
            'is-mobile': isMobile,
          })}
          slickTrackStyles={{
            width: `calc(${slideWidth}px * ${slidesToShow} + ${GAP} * ${slidesToShow - 2} - ${showArrowsWhenXl ? '0.25rem' : '1rem'})`,
            whiteSpace: 'nowrap',
          }}
          scrollSnapCenter={isMobile}
          scrollByParentWidth={isXlUp}
        >
          {isXlUp ? renderSlideMapping(slides.slice(1), true) : renderSlideMapping(slides)}
        </FilmstripCarousel>
      </Container>
    </article>
  );
}

BuyingGuideUI.propTypes = {
  content: CmsEntities.Content,
  isMobile: PropTypes.bool,
  breakpoint: PropTypes.string,
};

export const mapStateToProps = state => ({
  breakpoint: state.pageContext.breakpointDeprecated2,
});

export const BuyingGuide = connect(mapStateToProps)(BuyingGuideUI);
